import _ from 'lodash';

import './index.styl';
import template from './index.jst';
import { View } from "backbone.marionette";
import { Model } from 'backbone';
import { Preloader, ErrorsModal } from '../../../utils';
import { User as UserModel } from '../../../models';

const moment = require('moment');

export default class CoachView extends View {
	/**
	 * View template getter
	 * @return {Function} - lodash _.template function
	 */
	get template() {
		return template;
	}

	/**
	 * Root view tag class name
	 * @return {string}
	 */
	get className() {
		return 'account-coach shadow';
	}

	/**
	 * Hash of regions
	 * @return {{
	 *  header: string,
	 *  description: string,
	 *  coach_type: string,
	 *  features: string,
	 *  coach_form_table: string
	 * }}
	 */
	regions() {
		return {
			header: '.coach-header',
			description: '.coach-description',
			coach_type: '.coach-subscription-type',
			features: '.coach-features',
			coach_form_table: '.coach-form-table'
		}
	}

	/**
	 * Hash of child view events
	 * @return {{form:submit: string, plan:changed: string}}
	 */
	childViewEvents() {
		return {
			'form:submit': 'onFormSubmit',
			'form:code:submit': 'onFormCodeSubmit',
			'form:cancel:subscription:submit': 'cancelSubscription',
			'plan:changed': 'updateModel'
		}
	}

	/**
	 * Initialize view
	 * @description Add listeners to models. Request user and transaction history models.
	 * @return {CoachView}
	 */
	initialize() {
		_.extend(this, this.options);

		const plan = UserModel.get('assistant_coach_plan') || 'season';

		this.model = new Model({
			type: plan !== 'trial' && plan !== 'none' ? plan : 'season',
			message: false
		});

		this.listenTo(this, 'models:updated', this.initViews);
		this.listenTo(this.model, 'change:type', this.initViews);
		this.listenTo(UserModel, 'request:update:errors', ErrorsModal.show);
		this.listenTo(this.subscription, 'coach:subscribe:errors', ErrorsModal.show);
		this.listenTo(this.subscription, 'coach:unsubscribe:errors', ErrorsModal.show);

		this.updateModels(false);

		return this;
	}

	/**
	 * Update required for subscription models.
	 * @description Update user and transaction history models.
	 * @return {Promise.<CoachView>}
	 */
	async updateModels(is_on_success){
		await Promise.all([
			UserModel.update(),
		]);
		const response = await this.subscription.setTransactionHistoryUrl().getData({
			type: 'transaction:history'
		})
		if(is_on_success && response.success && response.result[0]){
			const transactionObject = _.get(response.result, '[0]');
			const {
				id,
				plan,
			} = transactionObject
			const costObj = {
				'trial': 0,
				'month': 4.99,
				'season': 19.99
			};
			window.gtag('event', 'purchase', {
				transaction_id: id,
				affiliation: "Fantasy Web | Coach Settings page",
				value: _.get(costObj, `[${plan}]`, 0),
				currency: "AUD",
				items: [
					{
						id: plan,
						name: `NRL Fantasy Coach - ${plan}`,
						category: "user"
					}
				]
			});
		}

		this.trigger('models:updated');

		return this;
	}

	/**
	 * Create header message, depend on assistant coach plan.
	 * @return {String} - header message
	 */
	getCoachPlanMessage() {
		const { assistant_coach, assistant_coach_plan } = UserModel.toJSON();

		let message = 'SUBSCRIBE';

		if (assistant_coach===1 && assistant_coach_plan!=='trial') {
			message = 'WELCOME';
		}

		return message + ' TO COACH';
	}

	/**
	 * Init regions with calculated models data
	 * @return {CoachView}
	 */
	initViews() {
		const transactions = _.chain(this.subscription.toJSON())
				.values()
				.sortBy('start')
				.map(transaction => ({
					...transaction,
					start: moment(transaction.start).format('DD.MM.YYYY'),
					end: moment(transaction.end).format('DD.MM.YYYY'),
				}))
				.value(),
			has_active_month_subscription = Boolean(_.find(
				transactions,
				{ status: 'approved', plan: 'month' }
			));

		if(has_active_month_subscription){
			const automatic_rollover = moment(_.last(transactions).end, 'DD.MM.YYYY')
				.add({ days: 1 })
				.format('DD/MM/YYYY');

			transactions.push({
				plan: `Automatic Rollover on ${automatic_rollover}`
			})
		}

		const
			assistant_coach_plan = UserModel.get('assistant_coach_plan'),
			{ trial_days_to_end, has_assistant_coach } = UserModel,
			coach_msg = this.getCoachPlanMessage(),
			selected_subscription_type = this.model.get('type'),
			is_plan_payed = (
				assistant_coach_plan === selected_subscription_type ||
				assistant_coach_plan === 'season'
			),
			params = {
				assistant_coach_plan,
				trial_end_in: trial_days_to_end,
				has_assistant_coach,
				transactions,
				selected_subscription_type,
				is_plan_payed,
				has_active_month_subscription
			};

		this.getChildView('header').init(coach_msg);
		this.getChildView('description').init(params);
		this.getChildView('coach_type').init(params);
		this.getChildView('coach_form_table').init(params);

		this.initFeaturesView(params);

		return this;
	}

	/**
	 * Update "features" region
	 * @param {Object} params - contain assistant_coach_plan, has_assistant_coach fields
	 */
	initFeaturesView(params) {
		this.getChildView('features').init(params);
	}

	/**
	 * Payment request
	 * @description Get token from Stripe API, pass token to BE, and updated models after that
	 * @param {Object} card - card object with name, number, cvv, exp_month, exp_year fields.
	 * @return {Promise.<CoachView>}
	 */
	async onFormSubmit(card) {
		Preloader.show();

		/**
		 * Request stripe token based on card data
		 */
		const { error, token } = await this.subscription.createStripeToken(card);

		if (error) {
			ErrorsModal.show(error.message);

			Preloader.hide();

			return this;
		}

		/**
		 * Call BE API to make payment with provided token
		 */
		const response = await this.subscribe({
			token: token.id,
			plan: this.model.get('type')
		});

		if (response.success) {
			/**
			 * Update user and transaction history models.
			 */
			await this.updateModels(true);
		}

		Preloader.hide();
	}

	/**
	 * Free code request
	 * @param code_coach
	 * @returns {Promise<void>}
	 */
	async onFormCodeSubmit(code) {
		Preloader.show();

		/**
		 * Call BE API to send subscribe Code
		 */
		const { success, errors } = await this.subscribeByeCode({
			code
		});

		if (success) {
			/**
			 * Update user and transaction history models.
			 */
			await this.updateModels(true);
		}
		else {
			ErrorsModal.show(errors);
		}

		Preloader.hide();
	}

	async cancelSubscription() {
		Preloader.show();

		/**
		 * Call BE API to cacncel subscription
		 */
		await this.unSubscribe();

		/**
		 * Update user and transaction history models.
		 */
		await this.updateModels(false);

		Preloader.hide();
	}

	/**
	 * Call BE API to cacncel subscription
	 * @return {Promise.<Subscription>}
	 */
	unSubscribe(){
		return this.subscription.setUnsubscribe().getData({
			type: 'coach:unsubscribe'
		});
	}

	/**
	 * Send subscribe request to BE
	 * @param {String} token - Stripe token
	 * @param {String} plan - "month", "season"
	 * @return {Promise.<Subscription>}
	 */
	subscribe({ token, plan }) {
		return this.subscription.setSubscribeUrl().postData({
			data: { token, plan },
			type: 'coach:subscribe'
		});
	}

	subscribeByeCode({ code }) {
		return this.subscription.setCodesubscribe().postData({
			data: { code },
			type: 'coach:code:subscribe'
		});
	}

	/**
	 * Update model assistant_coach_plan field
	 * @param {String} type - Assistant coach type
	 * @return {CoachView}
	 */
	updateModel(type) {
		this.model.set({ type });

		return this;
	}
}